// Student Management Component for Admin Dashboard
import React, { useState, useEffect } from 'react';
import ConfirmDialog from './confirmdialog';
import { API_BASE } from '../services/api.js';

const StudentManagement = ({ onBack }) => {
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteStudentId, setDeleteStudentId] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);

  const [newStudent, setNewStudent] = useState({
    name: '',
    email: '',
    password: '',
    current_level: 'beginner',
    academic_year: ''
  });

  const [accessForm, setAccessForm] = useState({
    subject_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    features: {
      teaching_mode: false,
      doubt_resolution: false,
      quiz: false,
      enhanced_learning: false
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load students
      const studentsResponse = await fetch(`${API_BASE}/students/`);
      const studentsData = await studentsResponse.json();
      setStudents(studentsData);

      // Load subjects
      const subjectsResponse = await fetch(`${API_BASE}/subjects/`);
      const subjectsData = await subjectsResponse.json();
      setSubjects(subjectsData);

      setLoading(false);
    } catch (error) {
      setError('Failed to load data');
      setLoading(false);
    }
  };

  const validateAcademicYear = (value) => {
    if (!value) return null;
    const match = value.match(/^(\d{4})-(\d{2})$/);
    if (!match) return 'Format must be YYYY-YY (e.g. 2025-26)';
    const start = parseInt(match[1]);
    const end = parseInt(match[2]);
    if (end !== (start + 1) % 100) return `End year must be ${String((start + 1) % 100).padStart(2, '0')} for start year ${start}`;
    return null;
  };
  
  
  
  const handleCreateStudent = async (e) => {
    e.preventDefault();
    setError('');
    const yearError = validateAcademicYear(newStudent.academic_year);
    if (yearError) { setError(yearError); return; }
    setSuccess('');

    try {
      const url = editingStudent 
        ? `${API_BASE}/students/${editingStudent.student_id}`
        : `${API_BASE}/students/`;
      
      const method = editingStudent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newStudent)
      });

      if (response.ok) {
        setSuccess(editingStudent ? 'Student updated successfully' : 'Student created successfully');
        setNewStudent({
          name: '',
          email: '',
          current_level: 'beginner',
          language_preference: 'english'
        });
        setShowAddForm(false);
        setEditingStudent(null);
        loadData();
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : `Failed to ${editingStudent ? 'update' : 'create'} student`);
      }
    } catch (error) {
      setError('Network error');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;

    try {
      const response = await fetch(`${API_BASE}/students/${studentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Student deleted successfully');
        loadData();
      } else {
        setError('Failed to delete student');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  const handleToggleStatus = async (studentId, currentStatus) => {
    try {
      const response = await fetch(`${API_BASE}/students/${studentId}/status?is_active=${!currentStatus}`, {
        method: 'PUT'
      });

      if (response.ok) {
        setSuccess(`Student ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        loadData();
      } else {
        setError('Failed to update student status');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setNewStudent({
      name: student.name,
      email: student.email,
      current_level: student.current_level,
      language_preference: student.language_preference,
      academic_year: student.academic_year || ''
    });
    setShowAddForm(true);
  };
  
  const handleGrantAccess = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Grant subject access
      if (accessForm.subject_id) {
        const subjectResponse = await fetch(`${API_BASE}/student-access/grant`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            student_id: selectedStudent.student_id,
            subject_id: parseInt(accessForm.subject_id),
            start_date: accessForm.start_date,
            end_date: accessForm.end_date || null,
            created_by: 'admin'
          })
        });

        if (!subjectResponse.ok) {
          throw new Error('Failed to grant subject access');
        }
      }

      // Grant feature access
      for (const [feature, granted] of Object.entries(accessForm.features)) {
        if (granted) {
          const featureResponse = await fetch(`${API_BASE}/student-access/grant-feature-access?student_id=${selectedStudent.student_id}&feature_name=${feature}&start_date=${accessForm.start_date}&created_by=admin`, {
            method: 'POST'
          });

          if (!featureResponse.ok) {
            console.warn(`Failed to grant ${feature} access`);
          }
        }
      }

      setSuccess('Access granted successfully');
      setShowAccessModal(false);
      setSelectedStudent(null);
      setAccessForm({
        subject_id: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        features: {
          teaching_mode: false,
          doubt_resolution: false,
          quiz: false,
          enhanced_learning: false
        }
      });
    } catch (error) {
      setError(error.message || 'Failed to grant access');
    }
  };

  // Add these functions here
const confirmDelete = async () => {
  try {
    const response = await fetch(`${API_BASE}/students/${deleteStudentId}`, {
      method: 'DELETE'
    });

    if (response.ok) {
      setSuccess('Student deleted successfully');
      loadData();
    } else {
      setError('Failed to delete student');
    }
  } catch (error) {
    setError('Network error');
  } finally {
    setShowConfirm(false);
    setDeleteStudentId(null);
  }
};

const cancelDelete = () => {
  setShowConfirm(false);
  setDeleteStudentId(null);
};

// Then immediately followed by:
if (loading) {
  return (
    <div className="admin-loading">
      <div className="loading-spinner"></div>
      <p>Loading students...</p>
    </div>
  );
}

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading students...</p>
      </div>
    );
  }

  return (
    <div className="student-management">
      {/* Header with Back Button */}
      <div className="management-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            ← Back to Dashboard
          </button>
          <h2>Student Management</h2>
        </div>
        <button 
          className="add-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Student'}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Add Student Form */}
      {showAddForm && (
        <div className="add-form-container">
          <h3>{editingStudent ? 'Edit Student' : 'Add New Student'}</h3>
          <form onSubmit={handleCreateStudent} className="add-form">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newStudent.name}
                  onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newStudent.email}
                  onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Password {editingStudent && <span style={{color:'#9ca3af', fontWeight:400, fontSize:'0.8rem'}}>(leave blank to keep unchanged)</span>}</label>
                <input
                  type="password"
                  value={newStudent.password}
                  onChange={(e) => setNewStudent({...newStudent, password: e.target.value})}
                  placeholder={editingStudent ? 'Leave blank to keep unchanged' : 'Enter password for student'}
                  required={!editingStudent}
                  minLength="6"
                />
              </div>
              <div className="form-group">
                <label>Level</label>    
                <select
                  value={newStudent.current_level}
                  onChange={(e) => setNewStudent({...newStudent, current_level: e.target.value})}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              
            </div>

            <div className="form-group">
                <label>Academic Year</label>
                <input
                  type="text"
                  value={newStudent.academic_year}
                  onChange={(e) => setNewStudent({...newStudent, academic_year: e.target.value})}
                  placeholder="e.g. 2024-25"
                />
                {newStudent.academic_year && validateAcademicYear(newStudent.academic_year) && (
                  <small style={{ color:'#ef4444', fontSize:'0.75rem' }}>
                    {validateAcademicYear(newStudent.academic_year)}
                  </small>
                )}
              </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {editingStudent ? 'Update Student' : 'Create Student'}
              </button>  
              <button type="button" className="cancel-btn" onClick={() => {
                setShowAddForm(false);
                setEditingStudent(null);
                setNewStudent({
                  name: '',
                  email: '',
                  current_level: 'beginner', 
                  academic_year: ''                 
                });
              }}>    
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Students Table */}
      <div className="students-table-container">
        <h3>All Students ({students.length})</h3>
        
        {students.length === 0 ? (
          <div className="no-data">
            <p>No students found. Add your first student!</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="students-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Level</th>
                  <th>Status</th>
                  <th>Academic Year</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => (
                  <tr key={student.student_id}>
                    <td>{student.student_id}</td>
                    <td>{student.name}</td>
                    <td>{student.email}</td>
                    <td>
                      <span className={`level-badge ${student.current_level}`}>
                        {student.current_level}
                      </span>
                    </td>
                    
                    <td>
                      <span className={`status-badge ${student.is_active ? 'active' : 'inactive'}`}>
                        {student.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{student.academic_year || '—'}</td>
                    <td>{new Date(student.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(student)}
                          title="Edit Student" 
                        >
                          ✏️
                        </button>
                        <button    
                          className={`action-btn ${student.is_active ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleStatus(student.student_id, student.is_active)}
                          title={student.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {student.is_active ? '🚫' : '✅'}
                        </button>
                        <button 
                          className="action-btn delete"
                          onClick={() => handleDeleteStudent(student.student_id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      
      {/* Confirm Delete Dialog */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <ConfirmDialog
              message="Are you sure you want to delete this student?"
              onConfirm={confirmDelete}
              onCancel={cancelDelete}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentManagement;